/**
 * screens/settings/plan.tsx — Plan & Usage (P-006).
 * Faithful RN port of design/plan.jsx PlanPage + PlanChangeModal: trial banner,
 * usage meters, the 4-tier comparison matrix, and upgrade/downgrade/cancel
 * flows. Wired to platformApi (changePlan / cancelSubscription).
 */

import React, { useState } from 'react';
import { View, Text, ScrollView } from 'react-native';

import { useTheme } from '../../context/themeContext';
import { usePlatform } from '../../context/platformContext';
import { useToast } from '../../components/toast';
import { SettingsHeader, Panel, Meter, Tag } from '../../components/platform/primitives';
import { PButton, PModal } from '../../components/platform/controls';
import Alert from '../../components/alert';
import Icon from '../../components/icon';
import { FS, FW, R, inr } from '../../utils/platformTokens';
import { changePlan, cancelSubscription } from '../../services/platform/platformApi';

type ModalState = { kind: 'upgrade' | 'downgrade' | 'cancel'; toPlan?: any } | null;

const PlanScreen: React.FC = () => {
  const { theme } = useTheme();
  const c = theme.colors;
  const { state, setState, role } = usePlatform();
  const toast = useToast();
  const canManage = role === 'ADMIN';
  const order = ['starter', 'standard', 'growth', 'scale'];
  const curIdx = order.indexOf(state.plan.current);
  const [modal, setModal] = useState<ModalState>(null);
  const fromPlan = state.plans.find((p) => p.id === state.plan.current);
  const pendingCancel = (state.plan as any).pendingCancel;

  const apply = async () => {
    if (!modal) return;
    if (modal.kind === 'cancel') {
      await cancelSubscription(state.org.name).catch(() => undefined);
      setState((s) => ({ ...s, plan: { ...s.plan, pendingCancel: true } as any }));
      toast.warn('Subscription set to cancel at period end.');
    } else if (modal.kind === 'upgrade') {
      await changePlan(state.org.name, 'sub', modal.toPlan.id).catch(() => undefined);
      setState((s) => ({ ...s, plan: { ...s.plan, current: modal.toPlan.id, trial: { ...s.plan.trial, active: false } } }));
      toast.success(`Upgraded to ${modal.toPlan.name}.`);
    } else {
      setState((s) => ({ ...s, plan: { ...s.plan, pendingDowngrade: modal.toPlan.id } as any }));
      toast.warn(`Downgrade to ${modal.toPlan.name} scheduled for next cycle.`);
    }
    setModal(null);
  };

  return (
    <ScrollView contentContainerStyle={{ padding: 28, maxWidth: 1100, width: '100%', alignSelf: 'center' }}>
      <SettingsHeader
        title="Plan & Usage"
        sub="Your subscription tier and how much of it you're using."
        action={canManage && !pendingCancel && (
          <PButton kind="ghost" textColor={c.danger} onPress={() => setModal({ kind: 'cancel' })}>Cancel subscription</PButton>
        )}
      />

      {state.plan.trial.active && (
        <View style={{ marginBottom: 20, flexDirection: 'row', alignItems: 'center', gap: 12, padding: 14, borderRadius: R.r3, backgroundColor: c.warnSoft, borderWidth: 1, borderColor: c.warnLine }}>
          <Icon name="clock" size={18} color={c.warn} />
          <Text style={{ flex: 1, color: c.warn, fontSize: FS.md }}>
            <Text style={{ fontWeight: FW.semibold }}>{state.plan.trial.daysLeft} days left in your free trial.</Text> Choose a plan to keep full access after it ends.
          </Text>
        </View>
      )}
      {pendingCancel && (
        <View style={{ marginBottom: 20 }}><Alert tone="danger" title="Subscription ending">Your plan is set to cancel on 01 Jul 2026.</Alert></View>
      )}

      <Panel title="Current usage" sub="Resets each billing cycle.">
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 24 }}>
          {state.usage.map((u) => (
            <View key={u.label} style={{ flexGrow: 1, minWidth: 220 }}>
              <Text style={{ fontSize: FS.sm, fontWeight: FW.medium, marginBottom: 8, color: c.ink }}>{u.label}</Text>
              <Meter used={u.used} cap={u.cap} unit={u.unit} />
            </View>
          ))}
        </View>
      </Panel>

      <Text style={{ fontSize: FS.lg, fontWeight: FW.semibold, color: c.ink, marginTop: 24, marginBottom: 12 }}>Compare plans</Text>
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        <View style={{ flexDirection: 'row', gap: 12, minWidth: 760 }}>
          {state.plans.map((p, i) => {
            const isCurrent = i === curIdx;
            const cta = isCurrent ? 'Current' : i > curIdx ? 'Upgrade' : 'Downgrade';
            return (
              <View
                key={p.id}
                style={{
                  width: 200, padding: 18, borderRadius: R.r3, borderWidth: isCurrent ? 2 : 1,
                  borderColor: isCurrent ? c.accent : c.line, backgroundColor: c.card, position: 'relative',
                }}
              >
                {(p.highlight && !isCurrent) && <View style={{ position: 'absolute', top: -10, left: 18 }}><Tag tone="accent" size="lg">Popular</Tag></View>}
                {isCurrent && <View style={{ position: 'absolute', top: -10, left: 18 }}><Tag tone="success" size="lg">Current plan</Tag></View>}
                <Text style={{ fontWeight: FW.semibold, fontSize: FS.xl, color: c.ink, marginTop: 6 }}>{p.name}</Text>
                <View style={{ flexDirection: 'row', alignItems: 'flex-end', marginVertical: 10 }}>
                  <Text style={{ fontSize: 24, fontWeight: FW.semibold, color: c.ink, fontFamily: theme.font.mono }}>{p.price === 0 ? 'Free' : inr(p.price)}</Text>
                  {p.price !== 0 && <Text style={{ color: c.ink3, fontSize: FS.sm, marginBottom: 3 }}>/mo</Text>}
                </View>
                <PButton
                  block
                  kind={isCurrent ? 'outline' : i > curIdx ? 'accent' : 'default'}
                  disabled={isCurrent || !canManage}
                  onPress={() => setModal({ kind: cta.toLowerCase() as any, toPlan: p })}
                >
                  {cta}
                </PButton>
                <View style={{ marginTop: 14, gap: 8 }}>
                  {state.planFeatures.map((f) => (
                    <View key={f.label} style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                      <Text style={{ color: c.ink3, fontSize: FS.sm }}>{f.label}</Text>
                      <Text style={{ fontWeight: FW.medium, fontSize: FS.sm, color: c.ink }}>{(f.values as any)[p.id]}</Text>
                    </View>
                  ))}
                </View>
              </View>
            );
          })}
        </View>
      </ScrollView>

      {!canManage && <View style={{ marginTop: 16 }}><Alert tone="info">You can view usage but only admins can change the plan.</Alert></View>}

      <PModal
        open={!!modal}
        onClose={() => setModal(null)}
        title={modal?.kind === 'cancel' ? 'Cancel subscription' : modal?.kind === 'upgrade' ? `Upgrade to ${modal?.toPlan?.name}` : `Downgrade to ${modal?.toPlan?.name}`}
        footer={<>
          <PButton kind="ghost" onPress={() => setModal(null)}>Keep current plan</PButton>
          <PButton kind={modal?.kind === 'upgrade' ? 'accent' : 'danger'} onPress={apply}>
            {modal?.kind === 'cancel' ? 'Cancel subscription' : modal?.kind === 'upgrade' ? 'Confirm & pay' : 'Schedule downgrade'}
          </PButton>
        </>}
      >
        {modal?.kind === 'upgrade' && (
          <View>
            <Text style={{ color: c.ink2, marginBottom: 12 }}>You'll move from {fromPlan?.name} to {modal.toPlan?.name} immediately.</Text>
            <View style={{ borderWidth: 1, borderColor: c.line, borderRadius: R.r3, padding: 14, backgroundColor: c.paper2, gap: 6 }}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                <Text style={{ color: c.ink3 }}>Prorated charge today</Text>
                <Text style={{ fontFamily: theme.font.mono, color: c.ink, fontWeight: FW.semibold }}>{inr(Math.round((modal.toPlan?.price - (fromPlan?.price || 0)) * 0.6))}</Text>
              </View>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                <Text style={{ color: c.ink3 }}>New rate from next cycle</Text>
                <Text style={{ fontFamily: theme.font.mono, color: c.ink, fontWeight: FW.semibold }}>{inr(modal.toPlan?.price)}/mo</Text>
              </View>
            </View>
          </View>
        )}
        {modal?.kind === 'downgrade' && (
          <View>
            <Text style={{ color: c.ink2, marginBottom: 12 }}>This change takes effect at the end of your current billing cycle (01 Jul 2026).</Text>
            <Alert tone="warn" title="What you'll lose">
              Contact capacity drops to {modal.toPlan?.contacts?.toLocaleString()}. Your workspace currently exceeds this — you'll need to archive contacts before the change applies.
            </Alert>
          </View>
        )}
        {modal?.kind === 'cancel' && (
          <View>
            <Text style={{ color: c.ink2, marginBottom: 12 }}>Your plan stays active until 01 Jul 2026. After that the workspace becomes read-only.</Text>
            <Alert tone="danger" title="90-day data retention">
              Your data is retained for 90 days after cancellation, then permanently deleted. Reactivate anytime within that window to restore full access.
            </Alert>
          </View>
        )}
      </PModal>
    </ScrollView>
  );
};

export default PlanScreen;
